import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Scissors, 
  Calendar, 
  Star, 
  Gift, 
  Camera,
  ChevronRight,
  Check,
  ArrowLeft,
  MapPin,
  Clock,
  Heart,
  Award,
  Search,
  CreditCard
} from "lucide-react";

const ClientLandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Calendar,
      title: "Easy Booking",
      description: "Book appointments with your favorite barbers and stylists in just a few taps."
    },
    {
      icon: Gift,
      title: "Loyalty Rewards",
      description: "Earn points with every visit and redeem them for discounts and free services."
    },
    {
      icon: Star,
      title: "Reviews & Ratings",
      description: "Read honest reviews and find the perfect stylist for your needs."
    },
    {
      icon: Camera,
      title: "Style Gallery",
      description: "Save photos of your favorite looks and share them with your stylist."
    },
    {
      icon: MapPin,
      title: "Find Nearby",
      description: "Discover top-rated barbers and stylists in your neighborhood."
    },
    {
      icon: Heart,
      title: "Favorites List",
      description: "Save your go-to stylists and rebook with them in seconds."
    }
  ];

  const benefits = [
    "No more waiting on hold to book",
    "Get reminders before your appointment",
    "Earn rewards with every visit",
    "Upload photos of styles you want",
    "Easy rebooking with your favorites",
    "Secure payment options"
  ];

  // Diverse client images - Black and Latino clients
  const clientImages = [
    "https://images.pexels.com/photos/3785079/pexels-photo-3785079.jpeg?auto=compress&w=400",
    "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&w=400",
    "https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&w=400"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-blue-100/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="w-5 h-5 text-gray-500" />
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Scissors className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-gray-800">MyHairChair</span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-gray-600 hover:text-blue-600">
                  Log In
                </Button>
              </Link>
              <Link to="/register?role=client">
                <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-lg shadow-blue-500/30">
                  Sign Up Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white border border-blue-200 px-4 py-2 rounded-full mb-8 shadow-sm">
                <Award className="w-4 h-4 text-blue-500" />
                <span className="text-blue-700 text-sm font-medium">For Clients</span>
              </div>
              
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 leading-tight">
                Book Your Next
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500"> Fresh Look</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Find top barbers and stylists near you. Book appointments, earn rewards, 
                and never have a bad hair day again.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to="/register?role=client">
                  <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white text-lg px-8 py-6 rounded-xl shadow-xl shadow-blue-500/30">
                    Get Started Free
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/find-stylists?type=stylist">
                  <Button variant="outline" className="border-blue-300 text-gray-700 hover:bg-blue-50 text-lg px-8 py-6 rounded-xl">
                    <Scissors className="w-5 h-5 mr-2" />
                    Browse Stylists
                  </Button>
                </Link>
                <Link to="/find-stylists?type=barber">
                  <Button variant="outline" className="border-amber-400 text-gray-700 hover:bg-amber-50 text-lg px-8 py-6 rounded-xl">
                    <Scissors className="w-5 h-5 mr-2" />
                    Browse Barbers
                  </Button>
                </Link>
              </div>

              {/* Social Proof */}
              <div className="flex items-center gap-4">
                <div className="flex -space-x-3">
                  {clientImages.map((img, i) => (
                    <img 
                      key={i}
                      src={img} 
                      alt="Client"
                      className="w-10 h-10 rounded-full border-2 border-white object-cover"
                    />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-500">Loved by 10,000+ clients</p>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-64 h-64 bg-gradient-to-br from-blue-200 to-cyan-200 rounded-full blur-3xl opacity-50"></div>
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-gradient-to-br from-cyan-200 to-blue-200 rounded-full blur-3xl opacity-50"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-blue-100">
                <img 
                  src="https://images.pexels.com/photos/3992874/pexels-photo-3992874.jpeg?auto=compress&w=600"
                  alt="Black male client getting a fresh haircut"
                  className="rounded-2xl w-full h-80 object-cover"
                />
                <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl p-4 border border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                      <Gift className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">250 Points</p>
                      <p className="text-sm text-gray-500">Earned this month</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-medium mb-4 block">Simple Process</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Getting your perfect style is just 4 easy steps away
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                icon: Search,
                title: "Find Your Stylist",
                description: "Browse barbers and stylists by location, specialty, ratings, and availability."
              },
              {
                step: "2",
                icon: Calendar,
                title: "Book Appointment",
                description: "Pick your service, choose a time slot, and confirm your booking in seconds."
              },
              {
                step: "3",
                icon: CreditCard,
                title: "Pay Securely",
                description: "Pay online or in-person with Stripe, PayPal, Cash App, Zelle, or cash."
              },
              {
                step: "4",
                icon: Gift,
                title: "Earn Rewards",
                description: "Collect points with every visit and redeem them for discounts and free services."
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                {index < 3 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-blue-300 to-transparent"></div>
                )}
                <div className="text-center">
                  <div className="relative inline-block mb-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200">
                      <item.icon className="w-10 h-10 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-blue-500">
                      <span className="text-blue-600 font-bold text-sm">{item.step}</span>
                    </div>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-gray-800 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/register?role=client">
              <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-6 rounded-xl text-lg">
                Get Started Free
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Everything You Need
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From booking to rewards, we've made getting styled easier than ever.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 border border-blue-100 hover:shadow-xl hover:shadow-blue-100/50 hover:-translate-y-1 transition-all"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-display text-xl font-semibold text-gray-800 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative">
              <h2 className="font-display text-3xl font-bold mb-8 text-center">
                Why Clients Love Us
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4" />
                    </div>
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="mt-10 text-center">
                <Link to="/register?role=client">
                  <Button className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-10 py-6 rounded-xl shadow-xl">
                    Create Free Account
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-blue-100 bg-white/50">
        <div className="max-w-6xl mx-auto text-center text-sm text-gray-500">
          © 2025 MyHairChair. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default ClientLandingPage;
